import { useRef, useState } from "react";
import { X, UploadCloud, Download, Loader2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";

const COLUMNS = [
  "Full Name", "Company Name", "Number of Employees", "Industry", "Website",
  "Company Linkedin URL", "Company Street", "Company City", "Company State/Province",
  "Company Country", "Company Postal Code/Zip Code", "Company Address", "Company Phone",
  "Contact Name", "Title", "Email", "Phone", "Lead Source", "Estimated Deal Value",
  "Status", "Tags", "Next Follow-up Date", "Notes",
];

export default function ImportModal({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dupMode, setDupMode] = useState("merge");
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = () => {
    const csv = COLUMNS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "king-of-it-leads-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("duplicate_mode", dupMode);
    try {
      const { data } = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [`${data.imported} imported`];
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.duplicates) parts.push(`${data.duplicates} duplicate(s) skipped`);
      if (data.skipped) parts.push(`${data.skipped} row(s) skipped`);
      if (data.blocked) parts.push(`${data.blocked} blocked (do-not-contact)`);
      toast.success(parts.join(" · "));
      onImported();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div data-testid="import-modal" className="bg-[#131C31] w-full max-w-lg rounded-2xl border border-slate-800 shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="font-display font-bold text-xl inline-flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-amber-400" /> Import Leads from CSV</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <button data-testid="download-template-button" onClick={downloadTemplate} className="w-full bg-slate-800 text-white font-semibold py-2.5 px-4 rounded-lg border border-slate-700 hover:bg-slate-700 text-sm inline-flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download CSV Template
          </button>

          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
          >
            <UploadCloud className="w-8 h-8 text-slate-500 mx-auto mb-3" />
            {file ? (
              <p className="text-sm text-amber-400 font-medium">{file.name}</p>
            ) : (
              <>
                <p className="text-sm text-slate-300 font-medium">Click to choose a .csv file</p>
                <p className="text-xs text-slate-500 mt-1">Column headers are matched automatically.</p>
              </>
            )}
            <input
              ref={fileRef}
              data-testid="import-file-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer hover:text-slate-300">Supported columns</summary>
            <p className="mt-2 leading-relaxed">{COLUMNS.join(" · ")}</p>
            <p className="mt-2">Rows without a Company Name (or Full Name) are skipped.</p>
          </details>

          <div>
            <p className="text-xs font-medium text-slate-400 mb-2">If a lead already exists (matched by email, then company name):</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="dup-mode-merge"
                onClick={() => setDupMode("merge")}
                className={`text-sm rounded-lg px-3 py-2 border transition-colors ${dupMode === "merge" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 bg-[#0B1120] text-slate-300 hover:bg-slate-800"}`}
              >
                Update existing
              </button>
              <button
                type="button"
                data-testid="dup-mode-skip"
                onClick={() => setDupMode("skip")}
                className={`text-sm rounded-lg px-3 py-2 border transition-colors ${dupMode === "skip" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 bg-[#0B1120] text-slate-300 hover:bg-slate-800"}`}
              >
                Skip duplicates
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              {dupMode === "merge"
                ? "Re-uploading a list updates matching leads instead of creating copies. Pipeline stage and tags are preserved."
                : "Matching leads are left untouched; only new leads are added."}
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="bg-slate-800 text-white font-semibold py-2 px-4 rounded-lg border border-slate-700 hover:bg-slate-700 text-sm">Cancel</button>
            <button data-testid="import-submit-button" onClick={upload} disabled={!file || uploading} className="btn-primary rounded-lg py-2 px-5 text-sm inline-flex items-center gap-2">
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />} Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
